import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import templatesAdminService, {
  type Template,
} from "../../../services/templatesAdminService";

export default function ListaTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroAtivo, setFiltroAtivo] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    const carregarTemplates = async () => {
      try {
        setLoading(true);
        const params =
          filtroAtivo !== undefined ? { ativo: filtroAtivo } : undefined;
        const response = await templatesAdminService.listar(params);
        setTemplates(response.templates);
      } catch (err) {
        console.error("Erro ao carregar templates:", err);
        toast.error("Erro ao carregar templates");
      } finally {
        setLoading(false);
      }
    };

    void carregarTemplates();
  }, [filtroAtivo]);

  const carregarTemplates = async () => {
    try {
      setLoading(true);
      const params =
        filtroAtivo !== undefined ? { ativo: filtroAtivo } : undefined;
      const response = await templatesAdminService.listar(params);
      setTemplates(response.templates);
    } catch (err) {
      console.error("Erro ao carregar templates:", err);
      toast.error("Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (id: number, ativo: boolean) => {
    try {
      if (ativo) {
        await templatesAdminService.desativar(id);
        toast.success("Template desativado com sucesso!");
      } else {
        await templatesAdminService.ativar(id);
        toast.success("Template ativado com sucesso!");
      }
      await carregarTemplates();
    } catch (err) {
      console.error("Erro:", err);
      toast.error("Erro ao alterar status do template");
    }
  };

  const handleDuplicar = async (id: number) => {
    if (!confirm("Deseja duplicar este template?")) return;

    try {
      await templatesAdminService.duplicar(id);
      toast.success("Template duplicado com sucesso!");
      await carregarTemplates();
    } catch (err) {
      console.error("Erro:", err);
      toast.error("Erro ao duplicar template");
    }
  };

  const handleExcluir = async (id: number) => {
    if (
      !confirm("Deseja excluir este template? Esta ação não pode ser desfeita.")
    )
      return;

    try {
      await templatesAdminService.excluir(id);
      toast.success("Template excluído com sucesso!");
      await carregarTemplates();
    } catch (err) {
      console.error("Erro:", err);
      toast.error(
        "Erro ao excluir template. Verifique se não há editais utilizando este template."
      );
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Templates de Edital
          </h1>
          <p className="mt-2 text-gray-600">
            Gerencie os modelos de formulário para editais
          </p>
        </div>
        <Link
          to="/admin/templates/novo"
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition font-medium"
        >
          + Novo Template
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFiltroAtivo(undefined)}
            className={`px-4 py-2 rounded-md transition ${
              filtroAtivo === undefined
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFiltroAtivo(true)}
            className={`px-4 py-2 rounded-md transition ${
              filtroAtivo === true
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Ativos
          </button>
          <button
            onClick={() => setFiltroAtivo(false)}
            className={`px-4 py-2 rounded-md transition ${
              filtroAtivo === false
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Inativos
          </button>
        </div>
      </div>

      {/* Lista */}
      {templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600">Nenhum template encontrado</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {template.nome}
                    </h2>
                    <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded">
                      v{template.versao}
                    </span>
                    <span
                      className={`text-sm px-3 py-1 rounded font-medium ${
                        template.ativo
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {template.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{template.descricao}</p>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span>
                      {Object.keys(template.schema_campos || {}).length} campos
                    </span>
                    {template.total_editais !== undefined && (
                      <span>{template.total_editais} editais usando</span>
                    )}
                    {template.criado_por && (
                      <span>Criado por: {template.criado_por.nome}</span>
                    )}
                  </div>
                </div>

                {/* Ações */}
                <div className="flex gap-2 ml-4">
                  <Link
                    to={`/admin/templates/${template.id}`}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition text-sm"
                  >
                    Ver Detalhes
                  </Link>
                  <Link
                    to={`/admin/templates/${template.id}/editar`}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition text-sm"
                  >
                    Editar
                  </Link>
                  <button
                    onClick={() => handleDuplicar(template.id)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition text-sm"
                  >
                    Duplicar
                  </button>
                  <button
                    onClick={() =>
                      handleToggleAtivo(template.id, template.ativo)
                    }
                    className={`px-4 py-2 rounded-md transition text-sm ${
                      template.ativo
                        ? "bg-orange-600 text-white hover:bg-orange-700"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {template.ativo ? "Desativar" : "Ativar"}
                  </button>
                  <button
                    onClick={() => handleExcluir(template.id)}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition text-sm"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
